import Util from "@/utils/util";
import { openProtocol } from "@babybus/lib-protocol";
import cookieJson from "virtual:dev-cookie";
import { useUserStore, useAnalysisStore } from "@/stores";
import AppConfig from "@/app.config";

// 计算版本ID
const verID = (() => {
  let verArr = AppConfig.viewVersion.split(".");
  for (let i = 0; i < verArr.length; i++) {
    if (i == 2) {
      verArr[i] = parseInt(verArr[i]) < 10 ? "0" + parseInt(verArr[i]) : verArr[i].toString();
    }
  }
  let id = verArr.join("");
  return parseInt(id);
})();

let _userStore: ReturnType<typeof useUserStore> | null = null;
let _analysisStore: ReturnType<typeof useAnalysisStore> | null = null;
const baseConfig = {
  appKey: "5B045E544EAC496B8A1251A2CC0005F7",
  appName: "小布咕商城-H5",
  autoGetOpenID: true,
  chCode: Util.getCHCode(),
  randomReportTime: 5,
  dataFlowAnalysis: false,
  isDebugMode: import.meta.env.VITE_APP_ENV != "master",
  isDebug: import.meta.env.VITE_APP_ENV != "master" ? 1 : 0,
  platform: 71,
  platform2: 18,
  productID: 2554,
  projectID: 384,
  verCode: AppConfig.viewVersion,
  verID: verID,
};

// AnalysisService - 核心埋点服务实现
const AnalysisService = {
  // 初始化埋点SDK
  init() {
    this.initSDK();
    console.log("埋点SDK初始化完成");
    return this;
  },

  async initSDK() {
    const initConfig: Record<string, any> = { ...baseConfig };

    // APP 环境：与原生账号体系关联
    if (Util.isInApp()) {
      try {
        const baseHeaderInfo = await openProtocol(`babybus://account/getBaseHeadInfo`, {
          headKeys: ["OpenID", "globalOffset", "AccountID"],
        });
        console.log("从APP获取的baseHeaderInfo:", baseHeaderInfo);

        if (baseHeaderInfo?.data?.globalOffset) {
          window.bbAnalysis.addHeaderInfo({
            // 设备偏移量，数字类型
            globalOffset: baseHeaderInfo.data.globalOffset,
          });
          initConfig.dataFlowAnalysis = true;
        }

        if (baseHeaderInfo?.data?.AccountID && baseHeaderInfo.data.AccountID != 0) {
          window.bbAnalysis.setUserInfo({ accountID: baseHeaderInfo.data.AccountID });
        } else {
          window.bbAnalysis.clearUserInfo();
        }

        if (baseHeaderInfo?.data?.OpenID) {
          initConfig.autoGetOpenID = false;
          window.bbAnalysis.setOpenID(baseHeaderInfo.data.OpenID);
        }
      } catch (error) {
        console.error("App 环境下获取头部字段失败：", error);
      }
    }

    // 小程序环境：与小程序 OpenID / AccountID 关联
    if (Util.isInMiniProgram()) {
      const OpenID = Util.getUrlParameterByName("OpenID", location.search);

      try {
        await _userStore?.fetchUserInfo();
        const accountID = _userStore?.userInfo?.accountID;
        if (accountID) {
          window.bbAnalysis.setUserInfo({ accountID });
        } else {
          window.bbAnalysis.clearUserInfo();
        }
      } catch (error) {
        console.error("小程序环境下获取用户信息失败：", error);
      }

      if (OpenID) {
        initConfig.autoGetOpenID = false;
        window.bbAnalysis.setOpenID(OpenID);
      } else {
        console.error("微信小程序环境下未获取到 OpenID");
      }
    }

    // 本地开发环境：使用 cookie.json 中的模拟 OpenID（仅浏览器直接调试场景，App/小程序容器不走这里）
    if (!Util.isInApp() && !Util.isInMiniProgram() && import.meta.env.VITE_APP_LOCAL_ENV == "dev") {
      const cookieUser = import.meta.env.VITE_APP_COOKIE_USER || "default";
      const envCookie = cookieJson[import.meta.env.VITE_APP_ENV];
      const OpenID = envCookie?.[cookieUser]?.find((item) => item.name == "OpenID")?.value;
      if (OpenID) {
        console.warn("当前环境为本地开发环境，使用模拟 OpenID", OpenID);
        initConfig.autoGetOpenID = false;
        window.bbAnalysis.setOpenID(OpenID);
      }
    }

    // 兜底：无论以上分支是否成功获取 OpenID（含协议调用异常），都保证 SDK 完成初始化；
    // 未显式设置 OpenID 时 autoGetOpenID 保持 true，由 SDK 自动生成，避免经分未初始化
    window.bbAnalysis.init(initConfig);
  },

  // 设置用户信息
  setUserInfo(userInfo) {
    window.bbAnalysis.setUserInfo(userInfo);
  },

  /**
   * 设置用户标签信息
   * @param userTagInfo - 用户标签信息对象
   * 格式示例:
   * {
   * Birthday: '出生日期（秒级时间戳），数字类型，选填',
   * Sex: '性别，字符串类型，选填',
   * Tag: {
   * [key](字符串): [value](字符串)
   * }
   */
  setUserTagInfo(userTagInfo) {
    window.bbAnalysis.updateUserTagInfo(userTagInfo);
  },

  setStore(userStore, analysisStore) {
    _userStore = userStore;
    _analysisStore = analysisStore;
  },

  // 获取公共参数
  getPublicParams() {
    let r: Record<string, any> = {};
    r.VipType = _userStore?.isVip ? "会员" : "非会员";
    r.IsLogin = _userStore?.isLogin ? "已登录" : "未登录";
    r.Source = _analysisStore?.source ? _analysisStore.source : "";
    r.ProductID = _analysisStore?.productID ? _analysisStore.productID : 0;
    r.Platform = _analysisStore?.platform ? _analysisStore.platform : "其他";
    r.src1 = _analysisStore?.src1 ? _analysisStore.src1 : "";
    r.src2 = _analysisStore?.src2 ? _analysisStore.src2 : "";
    r.src3 = _analysisStore?.src3 ? _analysisStore.src3 : "";
    r.src4 = _analysisStore?.src4 ? _analysisStore.src4 : "";
    return r;
  },

  // 埋点事件方法
  event(eventName, eventData = {}) {
    try {
      const params = this.getPublicParams();
      const platForm = Util.getEnvironment().isAndroid ? "android" : "ios";

      console.log("%c[埋点事件]", "color:#B0D656", eventName, platForm, {
        ...params,
        ...eventData,
      });

      window.bbAnalysis.event(
        {
          eventId: eventName,
          eventData: { ...params, ...eventData },
        },
        true,
      );
    } catch (e) {
      console.error("事件发送失败:", e);
    }

    return eventData;
  },
};

// 定义 Vue 插件
export default {
  install: (app) => {
    // 根据环境变量决定是否禁用埋点功能
    if (import.meta.env.VITE_DISABLE_ANALYSIS === "true") {
      console.log("Analysis plugin disabled");
      app.provide("$aiolosEvent", { event: () => {}, setUserTagInfo: () => {} });
      return;
    }
    // 获取 store 实例，须在 init 前注入，initSDK 异步流程中需要用到 _userStore
    const userStore = useUserStore();
    const analysisStore = useAnalysisStore();
    AnalysisService.setStore(userStore, analysisStore);

    // 初始化埋点SDK
    AnalysisService.init();

    // 添加全局方法（直接函数形式）
    const event = (eventName, params) => AnalysisService.event(eventName, params);
    const setUserTagInfo = (userTagInfo: Record<string, any>) => AnalysisService.setUserTagInfo(userTagInfo);

    // 提供依赖注入
    app.provide("$aiolosEvent", { event, setUserTagInfo });

    console.log("埋点插件注册成功");
  },
};

// 导出原始服务以便其他地方也可以使用
export { AnalysisService };
